/* eslint-disable react/prop-types */
import {
  Button,
  Form,
  Input,
  Modal,
  Progress,
  Radio,
  Select,
  Space,
  Spin,
  TimePicker,
  Tooltip,
  message,
} from "antd";
import dayjs from "dayjs";
// eslint-disable-next-line no-unused-vars
import React, { useEffect, useRef, useState } from "react";
import { t } from "ttag";

import Label from "metabase/common/components/type/Label";
import { getSpreadsheetCompatibleExportData } from "metabase/jerry/Common/export-data";
import { FormButton } from "metabase/jerry/style/FormButton.styled";
import { openInBlankWindow } from "metabase/lib/dom";
import { Icon } from "metabase/ui";

const { Option } = Select;

const apiRequest = async (type, payload) => {
  const response = await fetch(`/api/jerry/extend`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      call: "post",
      service_name: "spreadsheet",
      body: { type, payload },
      timeout: 120000,
    }),
  });

  const responseData = await response.json().catch(() => ({
    message: `Invalid JSON response from server. Status: ${response.status}`,
    status: false,
  }));

  if (!response.ok || responseData.status !== true) {
    throw new Error(
      responseData.message ||
        `API request failed with status ${response.status}.`,
    );
  }

  return responseData;
};

export const saveSubscriptionAPI = async (
  questionId,
  questionName,
  user,
  result,
  config,
) => {
  try {
    const submission_value = {
      spreadsheetName: config.spreadsheetName,
      recipients: config.recipients,
      schedule: {
        type: config.scheduleType,
        time: dayjs.isDayjs(config.time)
          ? config.time.format("HH:mm")
          : config.time,
        dayOfWeek: config.dayOfWeek,
        dayOfMonth: config.dayOfMonth,
      },
    };

    const payload = {
      report_type: "report",
      question_id: questionId,
      question_name: questionName,
      created_user: user,
      created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      submission_value: submission_value,
      url: window.location.href,
    };

    await apiRequest("config_schedule", payload);
    return { success: true };
  } catch (error) {
    message.error(`Failed to save subscription: ${error.message}`);
    return { success: false };
  }
};

export const firstExportAPI = async (
  questionId,
  questionName,
  user,
  result,
  config,
  exportData,
) => {
  try {
    const submission_value = {
      spreadsheetName: config.spreadsheetName,
      recipients: config.recipients,
      schedule: {
        type: config.scheduleType,
        time: dayjs.isDayjs(config.time)
          ? config.time.format("HH:mm")
          : config.time,
        dayOfWeek: config.dayOfWeek,
        dayOfMonth: config.dayOfMonth,
      },
    };

    const payload = {
      report_type: "report",
      question_id: questionId,
      question_name: questionName,
      created_user: user,
      created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      submission_value: submission_value,
      url: window.location.href,
      data: {
        ...result?.data,
        ...exportData,
      },
    };
    const responseData = await apiRequest("first_export", payload);
    return { success: true, url: responseData.url };
  } catch (error) {
    message.error(`Failed to save subscription: ${error.message}`);
    return { success: false };
  }
};

const testSubscriptionAPI = async (result, user, config, exportData) => {
  await new Promise((resolve) => setTimeout(resolve, 1500));
  try {
    const response = await fetch(`/api/jerry/extend`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        call: "post",
        service_name: "spreadsheet",
        body: {
          type: "test_schedule",
          payload: {
            ...config,
            data: {
              ...result?.data,
              ...exportData,
            },
            user: user,
            emails: config.recipients,
          },
        },
        timeout: 300000,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: "Unable to parse error response.",
      }));
      throw new Error(
        errorData.message ||
          `Export failed with HTTP status: ${response.status}`,
      );
    }

    const responseData = await response.json();
    if (responseData.status === true) {
      message.success("Test email sent successfully!");
      return { success: true };
    } else {
      throw new Error(
        responseData.message ||
          `Export failed with HTTP status: ${response.status}`,
      );
    }
  } catch (error) {
    message.error("Test email sent failed! Please try again.");
    return { success: false };
  }
};

function ScheduleSpreadsheet({
  question,
  result,
  user,
  users = [],
  isShowingRawTable,
  rawSeries,
  visualizationSettings,
  onModalStateChange,
}) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [scheduleType, setScheduleType] = useState("daily");
  const [form] = Form.useForm();

  const [isProgressModalVisible, setIsProgressModalVisible] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState("");
  const progressInterval = useRef(null);

  useEffect(() => {
    if (onModalStateChange) {
      onModalStateChange(isModalVisible);
    }
  }, [isModalVisible, onModalStateChange]);

  const startProgressSimulation = () => {
    setExportProgress(0);
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    progressInterval.current = setInterval(() => {
      setExportProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval.current);
          return 90;
        }
        return prev + 5;
      });
    }, 800);
  };

  const stopProgressSimulation = () => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
  };

  const showScheduleModal = async () => {
    setIsModalVisible(true);
    setIsLoading(true);
    try {
      const nowtime = dayjs().format("YYYY-MM-DD HH:mm");
      form.setFieldsValue({
        spreadsheetName: `Metabase Subscription - ${question.displayName()} - ${nowtime}`,
        scheduleType: "daily",
        recipients: [user.email],
        time: dayjs("00:00", "HH:mm"),
      });
      setScheduleType("daily");
    } catch (error) {
      console.error("Failed to fetch subscription settings:", error);
      message.error("Could not load subscription settings.");
      setIsModalVisible(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const exportData = getSpreadsheetCompatibleExportData({
        isShowingRawTable,
        result,
        visualizationSettings,
        rawSeries,
      });
      const submissionValues = {
        ...values,
        time: values.time.format("HH:mm"),
      };
      setIsSaving(true);
      setIsModalVisible(false);
      setIsProgressModalVisible(true);
      setProgressStatus("Saving subscription...");
      setExportProgress(0);
      const saveResponse = await saveSubscriptionAPI(
        question.id(),
        question.displayName(),
        user,
        result,
        submissionValues,
      );
      if (!saveResponse.success) {
        throw new Error("Failed to save the subscription settings.");
      }
      message.success("Subscription saved successfully!");
      setProgressStatus("Exporting data now, this may take a moment...");
      startProgressSimulation();
      const exportResponse = await firstExportAPI(
        question.id(),
        question.displayName(),
        user,
        result,
        values,
        exportData,
      );
      stopProgressSimulation();
      setExportProgress(100);

      if (exportResponse.success && exportResponse?.url) {
        setProgressStatus("Export complete!");
        message.success(
          "Data exported! The spreadsheet will open in a new tab.",
        );
        openInBlankWindow(exportResponse.url);
        setTimeout(() => {
          setIsProgressModalVisible(false);
        }, 1500);
      } else {
        throw new Error(
          exportResponse.data?.message ||
            "Export failed and no URL was returned.",
        );
      }
    } catch (error) {
      stopProgressSimulation();
      message.error(error.message || "An unexpected error occurred.");
      setProgressStatus(
        "An error occurred. Please close this window and try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      await form.validateFields();
      setIsTesting(true);
      const exportData = getSpreadsheetCompatibleExportData({
        isShowingRawTable,
        result,
        visualizationSettings,
        rawSeries,
      });
      await testSubscriptionAPI(
        result,
        user,
        form.getFieldsValue(),
        exportData,
      );
    } catch (errorInfo) {
      message.warning("Please fill out the form before testing.");
    } finally {
      setIsTesting(false);
    }
  };

  const renderScheduleOptions = () => (
    <Space>
      {scheduleType === "weekly" && (
        <Form.Item name="dayOfWeek" noStyle rules={[{ required: true }]}>
          <Select placeholder="Select day" style={{ width: 120 }}>
            <Option value="monday">{t`Monday`}</Option>
            <Option value="tuesday">{t`Tuesday`}</Option>
            <Option value="wednesday">{t`Wednesday`}</Option>
            <Option value="thursday">{t`Thursday`}</Option>
            <Option value="friday">{t`Friday`}</Option>
            <Option value="saturday">{t`Saturday`}</Option>
            <Option value="sunday">{t`Sunday`}</Option>
          </Select>
        </Form.Item>
      )}
      {scheduleType === "monthly" && (
        <Form.Item name="dayOfMonth" noStyle rules={[{ required: true }]}>
          <Select placeholder="Select day" style={{ width: 120 }}>
            {[...Array(28).keys()].map((i) => (
              <Option key={i + 1} value={i + 1}>{`Day ${i + 1}`}</Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item name="time" noStyle rules={[{ required: true }]}>
        <TimePicker format="HH:mm" />
      </Form.Item>
    </Space>
  );

  const currentUrl = window.location.href;
  const modalTitle = (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span>{t`Schedule Google Sheet Export`}</span>
      <Tooltip title="Export Metabase data link">
        <a
          href={currentUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", display: "flex" }}
        >
          <Icon name="info" />
        </a>
      </Tooltip>
    </div>
  );

  return (
    <div>
      <div style={{ width: "100%" }}>
        <FormButton
          onClick={showScheduleModal}
          className="hover-parent hover--inherit"
        >
          <Icon
            name="calendar"
            size={30}
            style={{
              marginLeft: "2px",
              marginRight: "10px",
              color: "#0088fffa",
            }}
          />
          <Label
            my={0}
            style={{ marginTop: "5px" }}
          >{t`Schedule Export`}</Label>
        </FormButton>
      </div>
      <Modal
        title={modalTitle}
        width="600px"
        centered
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={isSaving}
        footer={[
          <Button key="back" onClick={() => setIsModalVisible(false)}>
            {t`Cancel`}
          </Button>,
          <Button key="test" loading={isTesting} onClick={handleTest}>
            {t`Test`}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isSaving}
            onClick={handleOk}
          >
            {t`Save`}
          </Button>,
        ]}
        styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
      >
        <Spin spinning={isLoading} tip="Loading settings...">
          <Form form={form} layout="vertical" name="subscription_form">
            <Form.Item
              name="spreadsheetName"
              label="Spreadsheet Name"
              rules={[
                {
                  required: true,
                  message: "Please input the spreadsheet name!",
                },
              ]}
            >
              <Input placeholder="e.g., Weekly Sales Report" />
            </Form.Item>
            <Form.Item
              name="recipients"
              label="Notify Users"
              rules={[
                {
                  required: true,
                  message: "Please select at least one user!",
                  type: "array",
                },
              ]}
            >
              <Select mode="multiple" placeholder="Select users">
                {users.map((u) => (
                  <Option key={u.email} value={u.email}>
                    {u.common_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Schedule (UTC Timezone)">
              <Space.Compact>
                <Form.Item name="scheduleType" noStyle>
                  <Radio.Group
                    onChange={(e) => setScheduleType(e.target.value)}
                  >
                    <Radio.Button value="daily">{t`Daily`}</Radio.Button>
                    <Radio.Button value="weekly">{t`Weekly`}</Radio.Button>
                    <Radio.Button value="monthly">{t`Monthly`}</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                {renderScheduleOptions()}
              </Space.Compact>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      <Modal
        title="Creating Your Scheduled Export..."
        open={isProgressModalVisible}
        centered
        closable={false}
        footer={null}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <p style={{ marginBottom: "20px", fontSize: "16px" }}>
            {progressStatus}
          </p>
          <Progress percent={exportProgress} status="active" />
        </div>
      </Modal>
    </div>
  );
}

export default ScheduleSpreadsheet;
